// Story saas-1.17: modal de seleção de formato de exportação batch.
// Abre quando user seleciona >= 2 respostas e clica "Exportar selecionados".
// Card "Multi-página" ativo; card "Analítico com gráficos" disabled ("Em breve" genérico).
//
// Decisões Pax (gate v1.1):
// - OQ4: 2 cards lado-a-lado + botão Cancelar textual no footer
// - OQ6: label "Em breve" genérico (sem "Premium")

import React from 'react';
import { FileText, BarChart3, FileCode } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../Button';

export interface ExportFormatModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Número de respostas selecionadas — exibido no título do modal. */
  selectedCount: number;
  /** Chamado quando o user escolhe o formato "Multi-página". */
  onSelectMultiPage: () => void;
  /** Story saas-PDF-C: chamado quando o user escolhe "Analítico com gráficos". */
  onSelectAnalytical?: () => void;
  /** Story saas-1.18: chamado quando o user escolhe "Markdown (.md)". */
  onSelectMarkdown?: () => void;
}

export const ExportFormatModal: React.FC<ExportFormatModalProps> = ({
  isOpen,
  onClose,
  selectedCount,
  onSelectMultiPage,
  onSelectAnalytical,
  onSelectMarkdown,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Exportar ${selectedCount} resposta${selectedCount === 1 ? '' : 's'}`}
      size="lg"
    >
      <div className="space-y-4">
        <p className="text-sm text-neutral-400">
          Escolha o formato de exportação.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Card: Multi-página (ATIVO) */}
          <button
            type="button"
            onClick={onSelectMultiPage}
            className="group flex flex-col items-start gap-2 p-5 rounded-xl border border-neutral-700 bg-neutral-900/50 hover:border-primary-500/50 hover:bg-primary-500/5 transition-all text-left"
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-500/20 text-primary-400 group-hover:bg-primary-500/30 transition-colors">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white mb-1">Multi-página</p>
              <p className="text-xs text-neutral-400 leading-relaxed">
                Uma resposta por página, concatenadas em um único PDF com capa de resumo.
              </p>
            </div>
          </button>

          {/* Card: Analítico (ATIVO via saas-PDF-C — gate pdf.report business-only) */}
          <button
            type="button"
            onClick={onSelectAnalytical}
            disabled={!onSelectAnalytical}
            className="group flex flex-col items-start gap-2 p-5 rounded-xl border border-neutral-700 bg-neutral-900/50 hover:border-primary-500/50 hover:bg-primary-500/5 transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-500/20 text-primary-400 group-hover:bg-primary-500/30 transition-colors">
              <BarChart3 className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white mb-1">Analítico com gráficos</p>
              <p className="text-xs text-neutral-400 leading-relaxed">
                Relatório agregado com visualizações (barras, pizza, NPS) das respostas selecionadas.
              </p>
            </div>
          </button>

          {/* Card: Markdown (.md) — Story saas-1.18 */}
          <button
            type="button"
            onClick={onSelectMarkdown}
            disabled={!onSelectMarkdown}
            className="group flex flex-col items-start gap-2 p-5 rounded-xl border border-neutral-700 bg-neutral-900/50 hover:border-primary-500/50 hover:bg-primary-500/5 transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-500/20 text-primary-400 group-hover:bg-primary-500/30 transition-colors">
              <FileCode className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white mb-1">Markdown (.md)</p>
              <p className="text-xs text-neutral-400 leading-relaxed">
                Texto puro, leve, compatível com Obsidian, Notion, Git.
              </p>
            </div>
          </button>
        </div>

        {/* Footer textual — Cancelar */}
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </div>
    </Modal>
  );
};
